import React, { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Container,
  Typography,
  Alert,
  Box,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTeams } from "../hooks/useTeams";
import TeamLogo from "../components/TeamLogo";
import { buildTeamListRows } from "../utils/teamTableRows";
import { formatPlayerName } from "../utils/textUtils";
import { QUERY_PAGE_SIZE_OPTIONS } from "../utils/queryBuilder";
import {
  NAME_SORT_ORDER,
  STAT_SORT_ORDER,
  getResultsGridSx,
  sortedClassFor,
} from "../utils/resultsGrid";

const Teams = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [playerCountFilter, setPlayerCountFilter] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [sortModel, setSortModel] = useState([
    { field: "playerCount", sort: "desc" },
  ]);

  const { totalResults, isLoading, error, getFilteredTeams } = useTeams({
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
  });

  const sortedField = sortModel[0]?.field;

  const handleTeamClick = (teamName) => {
    navigate(`/app/teams/${encodeURIComponent(teamName)}`);
  };

  const handlePlayerClick = (playerId) => {
    navigate(`/app/player/${playerId}`);
  };

  const baseFilteredTeams = getFilteredTeams(searchTerm);
  const filteredTeams = baseFilteredTeams.filter((team) => {
    const playerCount = team.totalPlayers || 0;
    if (playerCountFilter === "small" && playerCount >= 1000) return false;
    if (playerCountFilter === "medium" && playerCount >= 1000) return false;
    if (playerCountFilter === "large" && playerCount < 1000) return false;
    return true;
  });

  const teamsData = useMemo(
    () => buildTeamListRows(filteredTeams),
    [filteredTeams]
  );

  const columns = useMemo(
    () => [
      {
        field: "teamName",
        headerName: "Team",
        flex: 1.2,
        minWidth: 200,
        headerClassName: sortedClassFor("teamName", sortedField),
        cellClassName: sortedClassFor("teamName", sortedField),
        sortingOrder: NAME_SORT_ORDER,
        renderCell: (params) => (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ minWidth: 0, height: "100%" }}
          >
            <TeamLogo teamName={params.value} size={32} alt="" />
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              color="primary.light"
              title={params.value}
            >
              {params.value}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "playerCount",
        headerName: "Players",
        width: 120,
        type: "number",
        align: "right",
        headerAlign: "right",
        headerClassName: sortedClassFor("playerCount", sortedField),
        cellClassName: sortedClassFor("playerCount", sortedField),
        sortingOrder: STAT_SORT_ORDER,
      },
      {
        field: "players",
        headerName: "Sample Players",
        flex: 1,
        minWidth: 280,
        sortable: false,
        renderCell: (params) => {
          const players = params.value || [];
          if (players.length === 0) {
            return "Click to view players";
          }
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {players.slice(0, 3).map((player) => (
                <Chip
                  key={player.playerId}
                  label={formatPlayerName(player)}
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlayerClick(player.playerId);
                  }}
                  sx={{ cursor: "pointer" }}
                />
              ))}
              {players.length > 3 && (
                <Chip
                  label={`+${players.length - 3} more`}
                  size="small"
                  variant="outlined"
                  color="secondary"
                />
              )}
            </Box>
          );
        },
      },
    ],
    [sortedField]
  );

  return (
    <Container maxWidth="lg" sx={{ py: 0 }}>
      <Typography variant="h3" component="h1" gutterBottom align="center">
        MLB Teams
      </Typography>

      <Typography
        variant="h6"
        gutterBottom
        align="center"
        color="text.secondary"
        sx={{ mb: 4 }}
      >
        All Major League Baseball Teams
      </Typography>

      <Box
        sx={{
          mb: 3,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <TextField
          label="Search Teams"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 200 }}
          placeholder="Search by team name..."
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Player Count</InputLabel>
          <Select
            value={playerCountFilter}
            label="Player Count"
            onChange={(e) => setPlayerCountFilter(e.target.value)}
          >
            <MenuItem value="">All Teams</MenuItem>
            <MenuItem value="small">Small (&lt; 1000 players)</MenuItem>
            <MenuItem value="medium">Medium (&lt; 1000 players)</MenuItem>
            <MenuItem value="large">Large (&gt; 1000 players)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading teams data
        </Alert>
      )}

      <Paper sx={{ overflow: "hidden" }}>
        <DataGrid
          rows={teamsData}
          columns={columns}
          loading={isLoading}
          pageSizeOptions={QUERY_PAGE_SIZE_OPTIONS}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          paginationMode="server"
          rowCount={totalResults}
          disableRowSelectionOnClick
          disableColumnMenu
          sortingMode="client"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          rowHeight={52}
          onRowClick={(params) => handleTeamClick(params.row.teamName)}
          sx={{ ...getResultsGridSx(), cursor: "pointer" }}
        />
      </Paper>
    </Container>
  );
};

export default Teams;
